import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { Badge, Card, finPost, useAsk, money } from "./shared";
import ExplainButton from "./ExplainPanel";

const Cls = ({ c }) => <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${c === "exact" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{c === "exact" ? "exact" : "suggested"}</span>;

function Allocations({ ask }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["fin-alloc-sugg"], queryFn: async () => (await api.get("/fin/allocation-suggestions")).data });
  const canAlloc = hasPerm(user, "payables.pay_execute");
  const apply = async (it, o) => {
    let reason = "";
    if (o.class !== "exact") {
      reason = await ask("Apply a suggested allocation", `${o.reason}. It isn't an exact match: write why you're confident it's right (kept in the audit trail).`);
      if (!reason) return;
    }
    if (await finPost(ask, `/vendor-payments/${it.payment_id}/allocate`, { lines: o.lines.map((l) => ({ bill_id: l.bill_id, amount: l.amount })), suggestion_class: o.class, review_reason: reason }, "Allocated")) {
      qc.invalidateQueries({ queryKey: ["fin-alloc-sugg"] });
    }
  };
  return (
    <Card className="space-y-2" testid="alloc-suggestions">
      <p className="font-semibold">Payments awaiting allocation</p>
      <p className="text-xs text-slate-500">{data?.note}</p>
      {!data?.items?.length && <p className="text-sm text-slate-400" data-testid="alloc-sugg-empty">No unallocated payments with open bills.</p>}
      {(data?.items || []).map((it) => (
        <div key={it.payment_id} className="rounded-lg border border-slate-100 p-3" data-testid={`alloc-sugg-${it.payment_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm"><span><b>{it.number}</b> · {it.vendor_name} · paid {it.paid_on} · ref {it.reference || "—"}</span><span>Unallocated <b>{money(it.unallocated)}</b></span></div>
          {it.ambiguous && <p className="mt-1 text-xs text-amber-700" data-testid={`alloc-ambiguous-${it.payment_id}`}>Several bills fit equally. Choose carefully.</p>}
          <ul className="mt-2 space-y-1">{it.options.map((o, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`alloc-opt-${it.payment_id}-${i}`}>
              <Cls c={o.class} /><span className="text-slate-400">{Math.round(o.confidence * 100)}%</span>
              <span className="min-w-0 flex-1">{o.lines.map((l) => `${l.number} (${l.vendor_invoice_no}) ${money(l.amount)}`).join(" + ")}<span className="block text-slate-500">{o.reason}</span></span>
              {canAlloc && <button className="rounded border border-slate-200 px-2 py-0.5 font-medium hover:bg-slate-50" onClick={() => apply(it, o)} data-testid={`alloc-apply-${it.payment_id}-${i}`}>Allocate…</button>}
            </li>))}</ul>
        </div>
      ))}
    </Card>
  );
}

function Duplicates() {
  const { data } = useQuery({ queryKey: ["fin-dups"], queryFn: async () => (await api.get("/fin/duplicates")).data });
  return (
    <Card className="space-y-2" testid="dup-card">
      <p className="font-semibold">Potential duplicates</p>
      <p className="text-xs text-slate-500">{data?.note}</p>
      {(data?.records || []).map((x) => (
        <div key={x.id} className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2 text-sm" data-testid={`dup-rec-${x.id}`}>
          <Badge s={x.severity} /><span className="min-w-0 flex-1"><b>{x.ref_label}</b> · {x.vendor_name} · {money(x.amount)}<span className="block text-xs text-slate-500">{x.category}: {x.detail}</span></span>
          <ExplainButton body={{ target: "exception", id: x.id }} testid={`dup-explain-${x.id}`} />
        </div>))}
      {(data?.vendors || []).map((g, i) => (
        <div key={i} className="border-t border-slate-100 pt-2 text-sm" data-testid={`dup-vendor-${i}`}>
          <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${g.strong ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>same {g.match}</span>
          {g.vendors.map((v) => `${v.legal_name} (${v.number || v.status})`).join(" · ")}
        </div>))}
      {!data?.records?.length && !data?.vendors?.length && <p className="text-sm text-slate-400" data-testid="dup-empty">No potential duplicates found.</p>}
    </Card>
  );
}

export default function MatchingTab() {
  const [ask, askNode] = useAsk();
  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-testid="matching-tab">
      <Allocations ask={ask} />
      <Duplicates />
      {askNode}
    </div>
  );
}
